import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { resizeImage } from "../../../services/screenshot.service"
import { useFloor } from "../../../store/floor/hooks"
import { useModal } from "../../../store/modal/hooks"
import { renderer } from "../../viewer/Renderer"
import { useApplication } from "../../../store/application/hooks"
import { isMobile } from "../../../services/helpers.service"
import { useAccountState } from "../../providers/AccountStateProvider"
import { useQueryParams } from "../../../store/query/floor.hooks"
import { FloorData } from "../../../store/floor/store"
import { Models } from "../../viewer/renderer.api"
import { ApplicationTypes } from "../../../store/application/store"

type VisibleState = {
  floorType: boolean
  floorScreed: boolean
  floorHeating: boolean
  floorInsulationFoil: boolean
  floorInsulation: boolean
  floorInsulationEdge: boolean
  bearingFloor: boolean
  details: boolean
}

export function useOverview() {
  const account = useAccountState()
  const { t, i18n } = useTranslation() as any
  const [, country] = i18n.language.split("_")
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const showExtendedThermalCalculation = country === "nl" && !isMobile()
  const {
    configurationState: { floorHeating, floorInsulation, floorScreed },
    thermalPerformance,
    setOverviewScreenshot,
    setHumanReadableName,
  } = useFloor()

  const floorHeatingType = floorHeating.type!
  const floorInsulationType = floorInsulation.type!
  const floorScreedType = floorScreed.type!

  const {
    displayFloorDownloadModal,
    displayFloorThermalCalculationModal,
    displayFloorExtendedThermalCalculationModal,
    displayRequestAdviceModal,
    displaySaveConfigurationModal,
    displaySaveConfigurationSuccessModal,
  } = useModal()
  const {
    currentModel,
    applicationType,
    directLink,
    setDirectLink,
  } = useApplication()
  const { setQueryParams } = useQueryParams()

  const initialVisibleState = useMemo(
    () => ({
      floorType: true,
      floorScreed: true,
      floorHeating: true,
      floorInsulationFoil: true,
      floorInsulation: true,
      floorInsulationEdge: true,
      bearingFloor: true,
      details: true,
    }),
    []
  )
  const [visibleState, setVisibleState] = useState<VisibleState>(
    initialVisibleState
  )
  const humanReadableName = `${floorInsulation.type} / ${floorScreed.type}`

  useEffect(() => {
    setHumanReadableName(humanReadableName)
  }, [humanReadableName, setHumanReadableName])

  useEffect(() => {
    if (!directLink) {
      const data: FloorData = {
        floorHeating,
        floorInsulation,
        floorScreed,
        thermalPerformance,
      }
      setQueryParams(data, i18n.language)
    }

    // Handle direct link
    if (renderer && currentModel === Models.DOLLHOUSE && directLink) {
      setDirectLink(false)
      setTimeout(() => {
        let model = Models.FLOOR
        renderer.displayModel(model).then(() => {
          renderer.displayFloorHeatingType(floorHeatingType)
          renderer.toggleLayer("VLOERVERWARMING", true)
          renderer.displayFloorInsulationType(floorInsulationType)
          renderer.toggleLayer("ISOLATIE", true)
          renderer.displayFloorScreedType(floorScreedType)
          renderer.toggleLayer("DEKVLOER", true)
        })
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    thermalPerformance,
    floorHeatingType,
    floorInsulationType,
    floorScreedType,
    setQueryParams,
    directLink,
  ])

  useEffect(
    () => {
      if (!isMobile()) renderer.displayMarkers()
      const dataURL = renderer.createScreenshot()
      resizeImage(dataURL, 520, 420).then((resizedDataURL) => {
        setOverviewScreenshot(resizedDataURL)
      })

      return () => {
        renderer.hideMarkers()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOverviewScreenshot]
  )

  function toggleVisibleState(key: keyof VisibleState) {
    setVisibleState({ ...visibleState, [key]: !visibleState[key] })
  }

  useEffect(() => {
    setVisibleState(initialVisibleState)
    if (!isMobile()) renderer.displayMarkers()
    renderer.toggleAllLayers(applicationType, true)
  }, [currentModel, setVisibleState, initialVisibleState, applicationType])

  function handleToggleClick(
    toggle = "floorHeating",
    layer = "VLOERVERWARMING"
  ) {
    toggleVisibleState(toggle as keyof VisibleState)
    renderer.toggleLayers(layer)
  }

  function handleDetailsClick() {
    toggleVisibleState("details")
    renderer.toggleDetails()
  }

  function handleAdjustSpecificationsClick() {
    if (showExtendedThermalCalculation) {
      displayFloorExtendedThermalCalculationModal()
    } else {
      displayFloorThermalCalculationModal()
    }
  }

  function handleDownloadFilesClick() {
    displayFloorDownloadModal()
  }

  function handleRequestAdviceClick() {
    displayRequestAdviceModal()
  }

  const copyShareLink = () => {
    const link = window.location.href
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => setLinkCopied(true))
    } else {
      window.prompt(t("flatRoof.overview.shareConfiguration"), link)
      setLinkCopied(true)
    }
  }

  function handleSaveConfigurationClick() {
    if (!account.isAuthenticated) return

    displaySaveConfigurationModal(async (name: string) => {
      const floorData: FloorData = {
        floorHeating: floorHeating,
        floorInsulation: floorInsulation,
        floorScreed: floorScreed,
        thermalPerformance: thermalPerformance,
      }
      const floorConfiguration = {
        application_type: ApplicationTypes.FLOOR,
        application_data: floorData,
        display_name: name ?? `${t("home.floor")}`,
        language: i18n.language,
      }
      account
        .saveConfiguration(floorConfiguration)
        .then((response) => {
          displaySaveConfigurationSuccessModal(
            response?.saved_configuration?.record_id
          )
        })
        .catch(() => {
          console.error("Failed to save configuration")
        })
    })
  }

  return {
    t,
    currentModel,
    thermalPerformance,
    visibleState,
    linkCopied,

    floorHeatingType,
    floorInsulationType,
    floorScreedType,

    handleToggleClick,
    handleDetailsClick,

    handleAdjustSpecificationsClick,
    handleDownloadFilesClick,
    handleRequestAdviceClick,

    copyShareLink,
    handleSaveConfigurationClick,
  }
}
